import { ReactNode, useContext, useMemo, SyntheticEvent } from "react";
import { Box, Typography, ListItemButton, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CheckCircle } from "@mui/icons-material";
import { ImportWizardContext } from "components/importer/ImportWizardContext";
import {
  getContrastColor,
  TEXT_PRIMARY,
  AUTURA_GRAYSCALE,
} from "context/themeContext";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { WIZARD_ACTION } from "types";
dayjs.extend(customParseFormat);

export const SystemFieldListItem = <F extends string>({
  systemField,
}: {
  systemField: F;
}): ReactNode => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const { state, dispatch, fieldMetadata } = useContext(ImportWizardContext);

  const csvField = state.columnMappings[systemField];
  const isConfirmed = state.confirmedFields.includes(systemField);
  const isSelected = state.selectedSystemField === systemField;
  const systemFieldText =
    t(`${systemField as string}`, { ns: "entities" }) +
    (fieldMetadata[systemField].required ? " *" : "");

  const columnMappingText: string = useMemo(() => {
    if (!csvField) {
      return "";
    }
    if (csvField.charAt(0) !== "_") {
      return csvField;
    }
    if (csvField.toLowerCase().startsWith("_generated")) {
      return t("auto_generated", { ns: "importWizard" });
    }
    if (csvField.toLowerCase().startsWith("_property")) {
      return t("property_address", { ns: "importWizard" });
    }
    if (csvField.toLowerCase().startsWith("_vehicle")) {
      return t("mapped_vehicle", { ns: "importWizard" });
    }
    if (csvField.toLowerCase().startsWith("_combined")) {
      return t("combined_field", { ns: "importWizard" });
    }
    return csvField;
  }, [csvField]);

  const handleListItemClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    dispatch({
      type: WIZARD_ACTION.SET_SELECTED_SYSTEM_FIELD,
      payload: systemField,
    });
  };

  return (
    <ListItemButton
      disableGutters
      sx={{
        border: "2px solid",
        borderColor: isConfirmed
          ? "success.main"
          : isSelected
            ? "primary.main"
            : "divider",
        borderRadius: 1,
        mb: 0.25,
        mx: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "row",
        height: 40,
        "& .arrow-box, & .dropdown": {
          transition: "background-color 0.3s ease-out",
        },
        "&:hover": {
          "& .arrow-box, & .dropdown": {
            backgroundColor: "#F6F6F6",
            transition: "background-color 0.1s ease-in",
          },
        },
      }}
      onClick={handleListItemClick}
    >
      <Box
        className="system-field"
        sx={{
          alignItems: "center",
          bgcolor: isSelected ? palette.primary.main : AUTURA_GRAYSCALE[300],
          p: 1.25,
          flex: 1,
          width: "100%",
          position: "relative",
        }}
      >
        <Typography
          sx={{
            color: isSelected
              ? getContrastColor(palette.primary.main)
              : TEXT_PRIMARY,
          }}
          fontWeight="bold"
          variant="subtitle2"
        >
          {systemFieldText}
        </Typography>
        <Box
          className="arrow-box"
          sx={{
            position: "absolute",
            right: -18,
            top: 0,
            backgroundColor: "white",
            width: 35,
            height: 35,
            rotate: "45deg",
            zIndex: 900,
          }}
        >
          {isConfirmed && (
            <CheckCircle
              color="success"
              sx={{ rotate: "-45deg", mt: 1, ml: 0.5, fontSize: 24 }}
            />
          )}
        </Box>
      </Box>
      <Typography
        variant="subtitle2"
        sx={{ flex: 1, textAlign: "right", pr: 2 }}
      >
        {columnMappingText}
      </Typography>
    </ListItemButton>
  );
};

export default SystemFieldListItem;
